import { useMemo } from "react";
import { SEOForm } from "../types";

export interface ChecklistItem {
  key: string;
  label: string;
  passed: boolean;
  passedText: string;
  failedText: string;
  suggestion: string;
  affectsScore?: boolean;
}

export interface SEOAnalysis {
  score: number;
  scoreLabel: "Excellent" | "Good" | "Needs Improvement" | "Poor";
  scoreColor: "success" | "info" | "warning" | "error";
  checklist: ChecklistItem[];
  suggestions: string[];
}

const TITLE_MIN = 30;
const TITLE_MAX = 60;
const DESCRIPTION_MIN = 120;
const DESCRIPTION_MAX = 160;

const includesKeyword = (text: string, keyword: string) =>
  Boolean(keyword.trim()) && text.toLowerCase().includes(keyword.trim().toLowerCase());

// Nothing here is "required" — every check is optional and just nudges the
// score up when satisfied. A blank page still scores something (robots and
// schema default to a passing state), and leaving the rest blank just caps
// the score lower rather than blocking save.
export const computeSEOAnalysis = (form: SEOForm): SEOAnalysis => {
  const titleLength = form.metaTitle.length;
  const descriptionLength = form.metaDescription.length;
  const titleOk = titleLength >= TITLE_MIN && titleLength <= TITLE_MAX;
  const descriptionOk = descriptionLength >= DESCRIPTION_MIN && descriptionLength <= DESCRIPTION_MAX;
  const focusKeywordSet = Boolean(form.focusKeyword.trim());
  const effectiveTwitterTitle = form.twitterTitle || form.ogTitle || form.metaTitle;
  const effectiveTwitterDescription = form.twitterDescription || form.ogDescription || form.metaDescription;

  const checklist: ChecklistItem[] = [
    {
      key: "titleLength",
      label: "Title Length",
      passed: titleOk,
      passedText: "Title length is optimal",
      failedText:
        titleLength === 0 ? "Title is empty" : titleLength < TITLE_MIN ? "Title is too short" : "Title is too long",
      suggestion: `Aim for ${TITLE_MIN}-${TITLE_MAX} characters in your meta title for optimal display.`,
    },
    {
      key: "descriptionLength",
      label: "Description Length",
      passed: descriptionOk,
      passedText: "Description length is optimal",
      failedText:
        descriptionLength === 0
          ? "Description is empty"
          : descriptionLength < DESCRIPTION_MIN
            ? "Description is too short"
            : "Description is too long",
      suggestion: `Meta description should be between ${DESCRIPTION_MIN}-${DESCRIPTION_MAX} characters for optimal display in search results.`,
    },
    {
      key: "focusKeyword",
      label: "Focus Keyword",
      passed: focusKeywordSet,
      passedText: "Focus keyword is set",
      failedText: "No focus keyword set",
      suggestion: "Set a focus keyword to target for this page.",
    },
    {
      key: "keywordInTitle",
      label: "Keyword in Title",
      passed: focusKeywordSet && includesKeyword(form.metaTitle, form.focusKeyword),
      passedText: "Focus keyword appears in title",
      failedText: "Focus keyword not in title",
      suggestion: "Include the focus keyword in your meta title for better relevance.",
    },
    {
      key: "relatedKeywords",
      label: "Related Keywords Set",
      passed: form.relatedKeywords.length > 0,
      passedText: "Yes",
      failedText: "No",
      suggestion: "",
      affectsScore: false,
    },
    {
      key: "ogImage",
      label: "Open Graph Image",
      passed: Boolean(form.ogImageUrl.trim()),
      passedText: "OG image is set",
      failedText: "OG image is not set",
      suggestion: "Add an Open Graph image so shared links show a preview image.",
    },
    {
      key: "twitterMeta",
      label: "Twitter Metadata",
      passed: Boolean(effectiveTwitterTitle.trim() && effectiveTwitterDescription.trim()),
      passedText: "Twitter metadata is set",
      failedText: "Twitter metadata is incomplete",
      suggestion: "Set a Twitter title and description so shared links render correctly on X.",
    },
    {
      key: "robots",
      label: "Robots Directives",
      passed: true,
      passedText: form.noindex || form.nofollow ? "Custom robots directives set" : "Default settings (index, follow)",
      failedText: "",
      suggestion: "",
    },
    {
      key: "schema",
      label: "Schema Markup",
      passed: Boolean(form.schemaType.trim()),
      passedText: "Schema type is set",
      failedText: "Schema type is not set",
      suggestion: "Choose a schema type so search engines can better understand this page's content.",
    },
  ];

  const scoredChecks = checklist.filter((item) => item.affectsScore !== false);
  const passedCount = scoredChecks.filter((item) => item.passed).length;
  const score = Math.round((passedCount / scoredChecks.length) * 100);

  const scoreLabel: SEOAnalysis["scoreLabel"] =
    score >= 90 ? "Excellent" : score >= 70 ? "Good" : score >= 50 ? "Needs Improvement" : "Poor";
  const scoreColor: SEOAnalysis["scoreColor"] =
    score >= 90 ? "success" : score >= 70 ? "info" : score >= 50 ? "warning" : "error";

  const suggestions = checklist.filter((item) => !item.passed && item.suggestion).map((item) => item.suggestion);

  return { score, scoreLabel, scoreColor, checklist, suggestions };
};

export const useSEOAnalysis = (form: SEOForm): SEOAnalysis => useMemo(() => computeSEOAnalysis(form), [form]);
